import React, { useEffect, useState, useCallback } from "react";
import {
  Table, Tag, Button, Drawer, Form, Input, Select, message, Space, Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import styled from "styled-components";
import customClientAxios from "../customAxios";

const TableCard = styled.div`
  background: white; border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0; overflow: hidden;
`;
const ToolbarRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px; flex-wrap: wrap; gap: 12px;
`;

const STATUS_COLORS = { active: "green", inactive: "default", suspended: "red" };
const ROLE_COLORS = { photographer: "blue", videographer: "purple", other: "default" };

function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchVendors = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await customClientAxios().get("/admin/photo-cloud/vendors", { params: { page, limit: 20 } });
      setVendors(res.data.vendors || []);
      setPagination({ page: res.data.pagination.page, total: res.data.pagination.total });
    } catch (e) {
      message.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const openCreate = () => {
    setEditingVendor(null);
    form.resetFields();
    form.setFieldsValue({ role: "photographer" });
    setDrawerOpen(true);
  };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
    form.setFieldsValue({
      name: vendor.name,
      email: vendor.email,
      phone: vendor.phone,
      role: vendor.role,
      company: vendor.company,
      status: vendor.status,
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingVendor) {
        await customClientAxios().put(`/admin/photo-cloud/vendors/${editingVendor.vendorId}`, values);
        message.success("Vendor updated");
      } else {
        await customClientAxios().post("/admin/photo-cloud/vendors", values);
        message.success("Vendor created — welcome email sent");
      }
      setDrawerOpen(false);
      fetchVendors(pagination.page);
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to save vendor");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (vendorId) => {
    const newPassword = prompt("Enter new password (min 8 chars):");
    if (!newPassword || newPassword.length < 8) {
      if (newPassword) message.error("Password must be at least 8 characters");
      return;
    }
    try {
      await customClientAxios().put(`/admin/photo-cloud/vendors/${vendorId}/reset-password`, { newPassword });
      message.success("Password reset — vendor notified by email");
    } catch (e) {
      message.error("Failed to reset password");
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", width: 180 },
    { title: "Email", dataIndex: "email", key: "email", width: 220 },
    { title: "Phone", dataIndex: "phone", key: "phone", width: 140 },
    {
      title: "Role", dataIndex: "role", key: "role", width: 120,
      render: (role) => <Tag color={ROLE_COLORS[role]}>{role}</Tag>,
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 100,
      render: (status) => <Tag color={STATUS_COLORS[status]}>{status}</Tag>,
    },
    { title: "Assignments", dataIndex: "totalAssignments", key: "totalAssignments", width: 100, align: "center" },
    { title: "Photos", dataIndex: "totalPhotosUploaded", key: "totalPhotosUploaded", width: 100, align: "center" },
    {
      title: "Actions", key: "actions", width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button>
          <Button size="small" onClick={() => handleResetPassword(record.vendorId)}>Reset Pwd</Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <ToolbarRow>
        <div style={{ fontSize: 13, color: "#64748b" }}>{pagination.total} vendors</div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Vendor</Button>
      </ToolbarRow>
      <TableCard>
        <Table
          dataSource={vendors}
          columns={columns}
          rowKey="vendorId"
          loading={loading}
          pagination={{
            current: pagination.page,
            total: pagination.total,
            pageSize: 20,
            showSizeChanger: false,
            onChange: (p) => fetchVendors(p),
          }}
          size="middle"
          scroll={{ x: 1100 }}
        />
      </TableCard>

      <Drawer
        title={editingVendor ? "Edit Vendor" : "Add Vendor"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={420}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
            <Input disabled={!!editingVendor} />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Role">
            <Select options={[
              { value: "photographer", label: "Photographer" },
              { value: "videographer", label: "Videographer" },
              { value: "other", label: "Other" },
            ]} />
          </Form.Item>
          <Form.Item name="company" label="Company">
            <Input />
          </Form.Item>
          {!editingVendor && (
            <Form.Item name="password" label="Password (auto-generated if empty)">
              <Input.Password />
            </Form.Item>
          )}
          {editingVendor && (
            <Form.Item name="status" label="Status">
              <Select options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
                { value: "suspended", label: "Suspended" },
              ]} />
            </Form.Item>
          )}
          <Button type="primary" htmlType="submit" loading={submitting} block>
            {editingVendor ? "Update" : "Create Vendor"}
          </Button>
        </Form>
      </Drawer>
    </>
  );
}

export default VendorManagement;
